<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchLevel,
  createLevel,
  updateLevel,
  fetchLevelConditions,
  fetchLevelCondition,
  createLevelCondition,
  updateLevelCondition,
  deleteLevelCondition,
} from '@apis/admin/security'
import { fetchCertificationTypes } from '@apis/admin/kyc'
import { fetchAssets } from '@apis/admin/asset'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem },
  props: {
    level_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      level_conditions: [],
      certification_types: [],
      assets: [],
      condition_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([
        this.loadLevel(),
        this.loadCertificationTypes(),
        this.loadAssets(),
      ])
        .then(() => this.loadLevelConditions())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadLevel() {
      const { level_info } = await fetchLevel(this.level_seq)
      this.params = defaults(level_info, {
        level: 0,
        title: '',
        description: '',
        list_order: 0,
        seq: null,
        regdate: null,
      })
      this.info = level_info
    },
    async loadLevelConditions() {
      if (!this.info) return []

      const { data } = await fetchLevelConditions(this.info.seq)

      this.level_conditions = data
    },
    async loadLevelCondition(condition_seq) {
      const { data } = await fetchLevelCondition(this.info.seq, condition_seq)

      this.condition_params = data
    },
    async loadCertificationTypes() {
      const { data } = await fetchCertificationTypes()

      this.certification_types = data
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateLevel : createLevel
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_level_modify')
        : this.$t('admin.msg_level_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddCondition() {
      this.level_conditions.push({
        list_order: 0,
        level_seq: this.info.seq,
        condition_type: undefined,
        certification_type_seq: undefined,
        asset_seq: undefined,
      })
    },
    async handleDeleteCondition(idx) {
      this.loading = true
      try {
        if (this.level_conditions[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteLevelCondition(
            this.info.seq,
            this.level_conditions[idx].seq
          )
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.level_conditions.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitCondition(idx) {
      this.loading = true
      const params = this.level_conditions[idx]
      ;(params.seq ? updateLevelCondition : createLevelCondition)(params)
        .then(({ seq }) => fetchLevelCondition(this.info.seq, seq))
        .then(({ condition_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.level_conditions, idx, { ...condition_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.level_title') }}</div></th
              >
              <td
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
              <th scope="row"
                ><div>{{ $t('admin.description') }}</div></th
              >
              <td
                ><LangItem
                  v-if="!loading"
                  v-model="params.description"
                ></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.level') }}</div></th
              >
              <td
                ><input
                  v-model="params.level"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
              <th scope="row"
                ><div>{{ $t('admin.form_info_list_order') }}</div></th
              >
              <td
                ><input
                  v-model="params.list_order"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('security.condition')"
      name="CONDITION"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">{{ $t('admin.level_list_order') }}</th>
            <th scope="col">{{ $t('admin.msg_level_condition') }}</th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddCondition"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(condition, idx) in level_conditions"
            :key="condition.seq ? `${condition.seq}` : `create-${idx}`"
          >
            <td :class="$style.tCenter">
              <input
                v-model="condition.list_order"
                type="text"
                :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              />
            </td>
            <td :class="$style.wide">
              <i18n
                :path="
                  `admin.level_condition_list.${condition.condition_type ||
                    'NONE'}`
                "
              >
                <select
                  v-model="condition.condition_type"
                  :disabled="condition.seq"
                >
                  <option :value="undefined">{{
                    $t('admin.restriction_select')
                  }}</option>
                  <option
                    v-for="[type, type_title] in constants.security.level
                      .condition_type"
                    :key="type"
                    :value="type"
                  >
                    {{ type_title }}
                  </option>
                </select>
                <template
                  v-if="condition.condition_type === 'KYC_CERTIFICATION'"
                >
                  <select v-model="condition.certification_type_seq">
                    <option :value="undefined">{{
                      $t('admin.restriction_select')
                    }}</option>
                    <option
                      v-for="{ seq, title } in certification_types"
                      :key="seq"
                      :value="seq"
                    >
                      {{ title }}
                    </option>
                  </select>
                </template>
                <template
                  v-else-if="
                    condition.condition_type ===
                      'KYC_ASSET_WITHDRAW_ADDRESS_CERTIFICATION'
                  "
                >
                  <select v-model="condition.asset_seq">
                    <option :value="undefined">{{
                      $t('admin.restriction_select')
                    }}</option>
                    <option
                      v-for="{ seq, title } in assets"
                      :key="seq"
                      :value="seq"
                    >
                      {{ title }}
                    </option>
                  </select>
                </template>
              </i18n>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="condition.seq"
                href="#"
                :class="[$style.button]"
                @click="handleSubmitCondition(idx)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              <a
                v-else
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitCondition(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteCondition(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!level_conditions.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('admin.no_level_condition')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
